import { Application } from '../../../_models/feature';

/**
 * BHT booking-portal catalogues — the Kategori -> Tjeneste cascade, one per
 * application that offers the Booking portal (Agil and dinHMS).
 *
 * GENERATED FROM Backend/Kuba2.Data.Services/Flexi/Onboarding/BhtOrderCatalog.cs.
 * The keys here MUST match that file exactly, per application: the backend
 * resolves them to the Norwegian labels it writes into the order email, and
 * rejects a pair it does not recognise. Labels on screen come from i18n; a key is never displayed.
 *
 * Service keys repeat across categories on purpose (an HSE audit sits under
 * both Systematic HSE work and HSEQ), which is why a service is only ever
 * resolved within its category — and why the i18n keys are scoped by category.
 */

export interface BhtServiceOption {
  /** Stable key sent to the backend. */
  key: string;
  /** i18n key for the option label. */
  labelKey: string;
}

export interface BhtCategoryOption {
  key: string;
  labelKey: string;
  /** Empty for "Annet" — the customer is told a case handler will call. */
  services: BhtServiceOption[];
}

/** Category with no service list; the form hides the service select for it. */
export const BHT_OTHER_CATEGORY_KEY = 'other';

/**
 * Agil's catalogue (application 9) — mirrors BhtOrderCatalog.Agil. Orders
 * against it open a case in Agil's ticketing system.
 */
export const AGIL_BHT_CATEGORIES: BhtCategoryOption[] = [
  {
    key: 'systematic-hse-work',
    labelKey: 'FLEXI_BOOKING_PORTAL.CATEGORIES.SYSTEMATIC_HSE_WORK',
    services: [
      { key: 'collaboration-meeting', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.SYSTEMATIC_HSE_WORK.COLLABORATION_MEETING' },
      { key: 'action-plan', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.SYSTEMATIC_HSE_WORK.ACTION_PLAN' },
      { key: 'amu-participation', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.SYSTEMATIC_HSE_WORK.AMU_PARTICIPATION' },
      { key: 'safety-inspection', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.SYSTEMATIC_HSE_WORK.SAFETY_INSPECTION' },
      { key: 'risk-assessment', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.SYSTEMATIC_HSE_WORK.RISK_ASSESSMENT' },
      { key: 'hse-advisory', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.SYSTEMATIC_HSE_WORK.HSE_ADVISORY' },
      { key: 'hse-audit', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.SYSTEMATIC_HSE_WORK.HSE_AUDIT' },
    ],
  },
  {
    key: 'hseq',
    labelKey: 'FLEXI_BOOKING_PORTAL.CATEGORIES.HSEQ',
    services: [
      { key: 'iso-45001', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.HSEQ.ISO_45001' },
      { key: 'iso-9001', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.HSEQ.ISO_9001' },
      { key: 'iso-14001', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.HSEQ.ISO_14001' },
      { key: 'project-resources', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.HSEQ.PROJECT_RESOURCES' },
      { key: 'hse-audit', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.HSEQ.HSE_AUDIT' },
      { key: 'management-systems', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.HSEQ.MANAGEMENT_SYSTEMS' },
      { key: 'hse-handbook', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.HSEQ.HSE_HANDBOOK' },
    ],
  },
  {
    key: 'occupational-health',
    labelKey: 'FLEXI_BOOKING_PORTAL.CATEGORIES.OCCUPATIONAL_HEALTH',
    services: [
      { key: 'doctor-appointment', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.OCCUPATIONAL_HEALTH.DOCTOR_APPOINTMENT' },
      { key: 'health-check', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.OCCUPATIONAL_HEALTH.HEALTH_CHECK' },
      { key: 'health-certificate', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.OCCUPATIONAL_HEALTH.HEALTH_CERTIFICATE' },
      { key: 'hearing-test', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.OCCUPATIONAL_HEALTH.HEARING_TEST' },
      { key: 'vaccination', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.OCCUPATIONAL_HEALTH.VACCINATION' },
      { key: 'substance-abuse-guidance', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.OCCUPATIONAL_HEALTH.SUBSTANCE_ABUSE_GUIDANCE' },
    ],
  },
  {
    key: 'psychosocial-work-environment',
    labelKey: 'FLEXI_BOOKING_PORTAL.CATEGORIES.PSYCHOSOCIAL_WORK_ENVIRONMENT',
    services: [
      { key: 'psychologist-appointment', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.PSYCHOSOCIAL_WORK_ENVIRONMENT.PSYCHOLOGIST_APPOINTMENT' },
      { key: 'leadership-guidance', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.PSYCHOSOCIAL_WORK_ENVIRONMENT.LEADERSHIP_GUIDANCE' },
      { key: 'conflict-management', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.PSYCHOSOCIAL_WORK_ENVIRONMENT.CONFLICT_MANAGEMENT' },
      { key: 'bullying-harassment', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.PSYCHOSOCIAL_WORK_ENVIRONMENT.BULLYING_HARASSMENT' },
      { key: 'work-environment-survey', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.PSYCHOSOCIAL_WORK_ENVIRONMENT.WORK_ENVIRONMENT_SURVEY' },
      { key: 'employee-survey-follow-up', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.PSYCHOSOCIAL_WORK_ENVIRONMENT.EMPLOYEE_SURVEY_FOLLOW_UP' },
      { key: 'crisis-preparedness', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.PSYCHOSOCIAL_WORK_ENVIRONMENT.CRISIS_PREPAREDNESS' },
    ],
  },
  {
    key: 'sick-leave-work',
    labelKey: 'FLEXI_BOOKING_PORTAL.CATEGORIES.SICK_LEAVE_WORK',
    services: [
      { key: 'expert-assistance', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.SICK_LEAVE_WORK.EXPERT_ASSISTANCE' },
      { key: 'sick-leave-prevention', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.SICK_LEAVE_WORK.SICK_LEAVE_PREVENTION' },
      { key: 'sick-leave-follow-up', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.SICK_LEAVE_WORK.SICK_LEAVE_FOLLOW_UP' },
      { key: 'dialogue-meeting', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.SICK_LEAVE_WORK.DIALOGUE_MEETING' },
      { key: 'function-assessment', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.SICK_LEAVE_WORK.FUNCTION_ASSESSMENT' },
      { key: 'womens-health', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.SICK_LEAVE_WORK.WOMENS_HEALTH' },
      { key: 'lifestyle-guidance', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.SICK_LEAVE_WORK.LIFESTYLE_GUIDANCE' },
    ],
  },
  {
    key: 'ergonomics',
    labelKey: 'FLEXI_BOOKING_PORTAL.CATEGORIES.ERGONOMICS',
    services: [
      { key: 'workplace-assessment', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.ERGONOMICS.WORKPLACE_ASSESSMENT' },
      { key: 'ergonomic-survey', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.ERGONOMICS.ERGONOMIC_SURVEY' },
      { key: 'ergonomics-training', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.ERGONOMICS.ERGONOMICS_TRAINING' },
    ],
  },
  {
    key: 'occupational-hygiene',
    labelKey: 'FLEXI_BOOKING_PORTAL.CATEGORIES.OCCUPATIONAL_HYGIENE',
    services: [
      { key: 'hygiene-survey', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.OCCUPATIONAL_HYGIENE.HYGIENE_SURVEY' },
      { key: 'hygiene-measurement', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.OCCUPATIONAL_HYGIENE.HYGIENE_MEASUREMENT' },
      { key: 'chemical-handling', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.OCCUPATIONAL_HYGIENE.CHEMICAL_HANDLING' },
    ],
  },
  {
    key: 'courses',
    labelKey: 'FLEXI_BOOKING_PORTAL.CATEGORIES.COURSES',
    services: [
      { key: 'hse-basic-course', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.COURSES.HSE_BASIC_COURSE' },
      { key: 'hse-course-managers', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.COURSES.HSE_COURSE_MANAGERS' },
      { key: 'first-aid-course', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.COURSES.FIRST_AID_COURSE' },
      { key: 'sick-leave', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.COURSES.SICK_LEAVE' },
      { key: 'ergonomics', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.COURSES.ERGONOMICS' },
      { key: 'risk-assessment', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.COURSES.RISK_ASSESSMENT' },
      { key: 'stress-management', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.COURSES.STRESS_MANAGEMENT' },
      { key: 'personnel-conflict-management', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.COURSES.PERSONNEL_CONFLICT_MANAGEMENT' },
      { key: 'violence-threats-compassion-fatigue', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.COURSES.VIOLENCE_THREATS_COMPASSION_FATIGUE' },
      { key: 'diisocyanates', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.COURSES.DIISOCYANATES' },
      { key: 'life-phase', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.COURSES.LIFE_PHASE' },
    ],
  },
  {
    key: 'other',
    labelKey: 'FLEXI_BOOKING_PORTAL.CATEGORIES.OTHER',
    services: [],
  },
];

/**
 * dinHMS's catalogue (application 3) — mirrors BhtOrderCatalog.DinHms. Orders
 * against it go to Kuba sales, never to Agil.
 *
 * Started as a copy of Agil's and kept as its own array, so either application
 * can add, drop or reorder a service without touching the other. A service
 * that exists only here gets its own i18n key rather than reusing an Agil one.
 */
export const DINHMS_BHT_CATEGORIES: BhtCategoryOption[] = [
  {
    key: 'systematic-hse-work',
    labelKey: 'FLEXI_BOOKING_PORTAL.CATEGORIES.SYSTEMATIC_HSE_WORK',
    services: [
      { key: 'collaboration-meeting', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.SYSTEMATIC_HSE_WORK.COLLABORATION_MEETING' },
      { key: 'action-plan', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.SYSTEMATIC_HSE_WORK.ACTION_PLAN' },
      { key: 'amu-participation', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.SYSTEMATIC_HSE_WORK.AMU_PARTICIPATION' },
      { key: 'safety-inspection', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.SYSTEMATIC_HSE_WORK.SAFETY_INSPECTION' },
      { key: 'risk-assessment', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.SYSTEMATIC_HSE_WORK.RISK_ASSESSMENT' },
      { key: 'hse-advisory', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.SYSTEMATIC_HSE_WORK.HSE_ADVISORY' },
      { key: 'hse-audit', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.SYSTEMATIC_HSE_WORK.HSE_AUDIT' },
    ],
  },
  {
    key: 'hseq',
    labelKey: 'FLEXI_BOOKING_PORTAL.CATEGORIES.HSEQ',
    services: [
      { key: 'iso-45001', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.HSEQ.ISO_45001' },
      { key: 'iso-9001', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.HSEQ.ISO_9001' },
      { key: 'iso-14001', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.HSEQ.ISO_14001' },
      { key: 'project-resources', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.HSEQ.PROJECT_RESOURCES' },
      { key: 'hse-audit', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.HSEQ.HSE_AUDIT' },
      { key: 'management-systems', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.HSEQ.MANAGEMENT_SYSTEMS' },
      { key: 'hse-handbook', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.HSEQ.HSE_HANDBOOK' },
    ],
  },
  {
    key: 'occupational-health',
    labelKey: 'FLEXI_BOOKING_PORTAL.CATEGORIES.OCCUPATIONAL_HEALTH',
    services: [
      { key: 'doctor-appointment', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.OCCUPATIONAL_HEALTH.DOCTOR_APPOINTMENT' },
      { key: 'health-check', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.OCCUPATIONAL_HEALTH.HEALTH_CHECK' },
      { key: 'health-certificate', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.OCCUPATIONAL_HEALTH.HEALTH_CERTIFICATE' },
      { key: 'hearing-test', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.OCCUPATIONAL_HEALTH.HEARING_TEST' },
      { key: 'vaccination', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.OCCUPATIONAL_HEALTH.VACCINATION' },
      { key: 'substance-abuse-guidance', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.OCCUPATIONAL_HEALTH.SUBSTANCE_ABUSE_GUIDANCE' },
    ],
  },
  {
    key: 'psychosocial-work-environment',
    labelKey: 'FLEXI_BOOKING_PORTAL.CATEGORIES.PSYCHOSOCIAL_WORK_ENVIRONMENT',
    services: [
      { key: 'psychologist-appointment', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.PSYCHOSOCIAL_WORK_ENVIRONMENT.PSYCHOLOGIST_APPOINTMENT' },
      { key: 'leadership-guidance', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.PSYCHOSOCIAL_WORK_ENVIRONMENT.LEADERSHIP_GUIDANCE' },
      { key: 'conflict-management', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.PSYCHOSOCIAL_WORK_ENVIRONMENT.CONFLICT_MANAGEMENT' },
      { key: 'bullying-harassment', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.PSYCHOSOCIAL_WORK_ENVIRONMENT.BULLYING_HARASSMENT' },
      { key: 'work-environment-survey', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.PSYCHOSOCIAL_WORK_ENVIRONMENT.WORK_ENVIRONMENT_SURVEY' },
      { key: 'employee-survey-follow-up', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.PSYCHOSOCIAL_WORK_ENVIRONMENT.EMPLOYEE_SURVEY_FOLLOW_UP' },
      { key: 'crisis-preparedness', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.PSYCHOSOCIAL_WORK_ENVIRONMENT.CRISIS_PREPAREDNESS' },
    ],
  },
  {
    key: 'sick-leave-work',
    labelKey: 'FLEXI_BOOKING_PORTAL.CATEGORIES.SICK_LEAVE_WORK',
    services: [
      { key: 'expert-assistance', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.SICK_LEAVE_WORK.EXPERT_ASSISTANCE' },
      { key: 'sick-leave-prevention', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.SICK_LEAVE_WORK.SICK_LEAVE_PREVENTION' },
      { key: 'sick-leave-follow-up', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.SICK_LEAVE_WORK.SICK_LEAVE_FOLLOW_UP' },
      { key: 'dialogue-meeting', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.SICK_LEAVE_WORK.DIALOGUE_MEETING' },
      { key: 'function-assessment', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.SICK_LEAVE_WORK.FUNCTION_ASSESSMENT' },
      { key: 'womens-health', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.SICK_LEAVE_WORK.WOMENS_HEALTH' },
      { key: 'lifestyle-guidance', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.SICK_LEAVE_WORK.LIFESTYLE_GUIDANCE' },
    ],
  },
  {
    key: 'ergonomics',
    labelKey: 'FLEXI_BOOKING_PORTAL.CATEGORIES.ERGONOMICS',
    services: [
      { key: 'workplace-assessment', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.ERGONOMICS.WORKPLACE_ASSESSMENT' },
      { key: 'ergonomic-survey', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.ERGONOMICS.ERGONOMIC_SURVEY' },
      { key: 'ergonomics-training', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.ERGONOMICS.ERGONOMICS_TRAINING' },
    ],
  },
  {
    key: 'occupational-hygiene',
    labelKey: 'FLEXI_BOOKING_PORTAL.CATEGORIES.OCCUPATIONAL_HYGIENE',
    services: [
      { key: 'hygiene-survey', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.OCCUPATIONAL_HYGIENE.HYGIENE_SURVEY' },
      { key: 'hygiene-measurement', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.OCCUPATIONAL_HYGIENE.HYGIENE_MEASUREMENT' },
      { key: 'chemical-handling', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.OCCUPATIONAL_HYGIENE.CHEMICAL_HANDLING' },
    ],
  },
  {
    key: 'courses',
    labelKey: 'FLEXI_BOOKING_PORTAL.CATEGORIES.COURSES',
    services: [
      { key: 'hse-basic-course', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.COURSES.HSE_BASIC_COURSE' },
      { key: 'hse-course-managers', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.COURSES.HSE_COURSE_MANAGERS' },
      { key: 'first-aid-course', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.COURSES.FIRST_AID_COURSE' },
      { key: 'sick-leave', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.COURSES.SICK_LEAVE' },
      { key: 'ergonomics', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.COURSES.ERGONOMICS' },
      { key: 'risk-assessment', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.COURSES.RISK_ASSESSMENT' },
      { key: 'stress-management', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.COURSES.STRESS_MANAGEMENT' },
      { key: 'personnel-conflict-management', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.COURSES.PERSONNEL_CONFLICT_MANAGEMENT' },
      { key: 'violence-threats-compassion-fatigue', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.COURSES.VIOLENCE_THREATS_COMPASSION_FATIGUE' },
      { key: 'diisocyanates', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.COURSES.DIISOCYANATES' },
      { key: 'life-phase', labelKey: 'FLEXI_BOOKING_PORTAL.SERVICES.COURSES.LIFE_PHASE' },
    ],
  },
  {
    key: 'other',
    labelKey: 'FLEXI_BOOKING_PORTAL.CATEGORIES.OTHER',
    services: [],
  },
];

/**
 * Catalogue for the signed-in application, or an empty list when it has none.
 * The backend resolves keys against the same application's catalogue only, so
 * a key from the other list would be refused on submit.
 */
export function getBhtCategories(applicationId: number | null | undefined): BhtCategoryOption[] {
  switch (Number(applicationId)) {
    case Application.AGIL:
      return AGIL_BHT_CATEGORIES;
    case Application.DIN_HMS:
      return DINHMS_BHT_CATEGORIES;
    default:
      return [];
  }
}
